import { CustomerAgeingData } from "@/types/ageing-analysis";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  MoreHorizontal,
  ArrowUpDown,
  Flag,
  Calendar,
  FolderOpen,
  Bell,
  MessageSquare,
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";

type SortField = "customerName" | "totalOutstanding" | "current" | "days31_60" | "days61_90" | "days91_120" | "over120" | "worstDaysOverdue" | "lastContactDate" | "riskLevel" | "aiPriorityScore" | "collectionStrategy";
type SortDirection = "asc" | "desc" | null;

interface AgeingAnalysisTableProps {
  customers: CustomerAgeingData[];
  selectedCustomerIds: Set<string>;
  visibleColumns: Record<string, boolean>;
  sortField: SortField | null;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
  onSelectAll: (checked: boolean) => void;
  onSelectCustomer: (customerId: string, checked: boolean) => void;
  onLogCall: (customer: CustomerAgeingData) => void;
  onSendEmail: (customer: CustomerAgeingData) => void;
  onCreatePromise: (customer: CustomerAgeingData) => void;
  onAssignClerk: (customer: CustomerAgeingData) => void;
  onViewCustomer: (customer: CustomerAgeingData) => void;
  formatCurrency: (amount: number) => string;
  getRiskBadgeColor: (risk: string) => string;
}

export function AgeingAnalysisTable({
  customers,
  selectedCustomerIds,
  visibleColumns,
  sortField,
  sortDirection,
  onSort,
  onSelectAll,
  onSelectCustomer,
  onLogCall,
  onSendEmail,
  onCreatePromise,
  onAssignClerk,
  onViewCustomer,
  formatCurrency,
  getRiskBadgeColor,
}: AgeingAnalysisTableProps) {
  const allSelected = customers.length > 0 && customers.every(c => selectedCustomerIds.has(c.id));
  const someSelected = customers.some(c => selectedCustomerIds.has(c.id)) && !allSelected;

  const SortableHeader = ({ field, children, className }: { field: SortField; children: React.ReactNode; className?: string }) => (
    <TableHead className={className}>
      <Button
        variant="ghost"
        size="sm"
        className="-ml-3 h-8 data-[state=open]:bg-accent"
        onClick={() => onSort(field)}
      >
        {children}
        <ArrowUpDown className="ml-2 h-4 w-4" />
      </Button>
    </TableHead>
  );

  if (customers.length === 0) {
    return (
      <div className="border rounded-lg p-12 text-center">
        <p className="text-muted-foreground">No customers found matching your filters</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">
              <Checkbox
                checked={allSelected}
                onCheckedChange={onSelectAll}
                aria-label="Select all"
                className={someSelected ? "data-[state=checked]:bg-primary" : ""}
              />
            </TableHead>
            <SortableHeader field="customerName">Customer Name</SortableHeader>
            {visibleColumns.totalOutstanding && (
              <SortableHeader field="totalOutstanding" className="text-right">
                Total Outstanding
              </SortableHeader>
            )}
            {visibleColumns.current && (
              <SortableHeader field="current" className="text-right">
                Current (0-30)
              </SortableHeader>
            )}
            {visibleColumns.days31_60 && (
              <SortableHeader field="days31_60" className="text-right">
                31-60 Days
              </SortableHeader>
            )}
            {visibleColumns.days61_90 && (
              <SortableHeader field="days61_90" className="text-right">
                61-90 Days
              </SortableHeader>
            )}
            {visibleColumns.days91_120 && (
              <SortableHeader field="days91_120" className="text-right">
                91-120 Days
              </SortableHeader>
            )}
            {visibleColumns.over120 && (
              <SortableHeader field="over120" className="text-right">
                Over 120 Days
              </SortableHeader>
            )}
            {visibleColumns.worstDaysOverdue && (
              <SortableHeader field="worstDaysOverdue" className="text-center">
                Worst Days Overdue
              </SortableHeader>
            )}
            {visibleColumns.lastContactDate && (
              <SortableHeader field="lastContactDate">Last Contact</SortableHeader>
            )}
            {visibleColumns.riskLevel && (
              <SortableHeader field="riskLevel">Risk Level</SortableHeader>
            )}
            {visibleColumns.aiPriorityScore && (
              <SortableHeader field="aiPriorityScore" className="text-center">
                AI Priority Score
              </SortableHeader>
            )}
            {visibleColumns.collectionStrategy && (
              <SortableHeader field="collectionStrategy">Collection Strategy</SortableHeader>
            )}
            {visibleColumns.statusFlags && (
              <TableHead className="text-center">Status Indicators</TableHead>
            )}
            <TableHead className="w-12">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {customers.map((customer) => (
            <TableRow key={customer.id} className="hover:bg-muted/50">
              <TableCell>
                <Checkbox
                  checked={selectedCustomerIds.has(customer.id)}
                  onCheckedChange={(checked) => onSelectCustomer(customer.id, checked as boolean)}
                  aria-label={`Select ${customer.customerName}`}
                />
              </TableCell>
              <TableCell>
                <button
                  onClick={() => onViewCustomer(customer)}
                  className="text-left hover:underline"
                >
                  <div className="font-medium">{customer.customerName}</div>
                  <div className="text-sm text-muted-foreground">{customer.accountNumber}</div>
                </button>
              </TableCell>
              {visibleColumns.totalOutstanding && (
                <TableCell className="text-right font-bold">
                  {formatCurrency(customer.totalOutstanding)}
                </TableCell>
              )}
              {visibleColumns.current && (
                <TableCell className="text-right">
                  <div>{formatCurrency(customer.ageing.current)}</div>
                  <Progress
                    value={(customer.ageing.current / customer.totalOutstanding) * 100}
                    className="h-1 mt-1"
                    accent="success"
                  />
                </TableCell>
              )}
              {visibleColumns.days31_60 && (
                <TableCell className="text-right">
                  <div>{formatCurrency(customer.ageing.days31_60)}</div>
                  <Progress
                    value={(customer.ageing.days31_60 / customer.totalOutstanding) * 100}
                    className="h-1 mt-1"
                    accent="warning"
                  />
                </TableCell>
              )}
              {visibleColumns.days61_90 && (
                <TableCell className="text-right">
                  <div>{formatCurrency(customer.ageing.days61_90)}</div>
                  <Progress
                    value={(customer.ageing.days61_90 / customer.totalOutstanding) * 100}
                    className="h-1 mt-1 bg-orange-100 [&>div]:bg-orange-500"
                  />
                </TableCell>
              )}
              {visibleColumns.days91_120 && (
                <TableCell className="text-right">
                  <div>{formatCurrency(customer.ageing.days91_120)}</div>
                  <Progress
                    value={(customer.ageing.days91_120 / customer.totalOutstanding) * 100}
                    className="h-1 mt-1"
                    accent="destructive"
                  />
                </TableCell>
              )}
              {visibleColumns.over120 && (
                <TableCell className="text-right">
                  <div>{formatCurrency(customer.ageing.over120)}</div>
                  <Progress
                    value={(customer.ageing.over120 / customer.totalOutstanding) * 100}
                    className="h-1 mt-1"
                    accent="destructive"
                  />
                </TableCell>
              )}
              {visibleColumns.worstDaysOverdue && (
                <TableCell className="text-center">
                  <Badge
                    variant="outline"
                    className={cn(
                      customer.worstDaysOverdue > 90
                        ? "border-red-600 text-red-600 bg-red-50"
                        : customer.worstDaysOverdue > 60
                        ? "border-yellow-600 text-yellow-600 bg-yellow-50"
                        : "border-gray-400 text-gray-600 bg-gray-50"
                    )}
                  >
                    {customer.worstDaysOverdue} days
                  </Badge>
                </TableCell>
              )}
              {visibleColumns.lastContactDate && (
                <TableCell>
                  {format(new Date(customer.lastContactDate), "MMM dd, yyyy")}
                </TableCell>
              )}
              {visibleColumns.riskLevel && (
                <TableCell>
                  <Badge className={getRiskBadgeColor(customer.riskLevel)}>
                    {customer.riskLevel.charAt(0).toUpperCase() + customer.riskLevel.slice(1)}
                  </Badge>
                </TableCell>
              )}
              {visibleColumns.aiPriorityScore && (
                <TableCell>
                  <div className="flex flex-col items-center gap-1">
                    <span className="text-sm font-medium">{customer.aiPriorityScore}</span>
                    <Progress
                      value={customer.aiPriorityScore}
                      className="h-1 w-16"
                    />
                  </div>
                </TableCell>
              )}
              {visibleColumns.collectionStrategy && (
                <TableCell>
                  <Badge variant="outline">{customer.collectionStrategy}</Badge>
                </TableCell>
              )}
              {visibleColumns.statusFlags && (
                <TableCell>
                  <TooltipProvider>
                    <div className="flex justify-center gap-1">
                      {customer.statusFlags.hasDispute && (
                        <Tooltip>
                          <TooltipTrigger>
                            <Flag className="h-4 w-4 text-red-600" />
                          </TooltipTrigger>
                          <TooltipContent>Has Dispute</TooltipContent>
                        </Tooltip>
                      )}
                      {customer.statusFlags.hasPromiseToPay && (
                        <Tooltip>
                          <TooltipTrigger>
                            <Calendar className="h-4 w-4 text-blue-600" />
                          </TooltipTrigger>
                          <TooltipContent>Has Promise to Pay</TooltipContent>
                        </Tooltip>
                      )}
                      {customer.statusFlags.hasCollectionCase && (
                        <Tooltip>
                          <TooltipTrigger>
                            <FolderOpen className="h-4 w-4 text-orange-600" />
                          </TooltipTrigger>
                          <TooltipContent>Has Collection Case</TooltipContent>
                        </Tooltip>
                      )}
                      {customer.statusFlags.isOverdueAlert && (
                        <Tooltip>
                          <TooltipTrigger>
                            <Bell className="h-4 w-4 text-red-600" />
                          </TooltipTrigger>
                          <TooltipContent>Overdue Alert</TooltipContent>
                        </Tooltip>
                      )}
                      {customer.statusFlags.noRecentContact && (
                        <Tooltip>
                          <TooltipTrigger>
                            <MessageSquare className="h-4 w-4 text-gray-600" />
                          </TooltipTrigger>
                          <TooltipContent>No Recent Contact</TooltipContent>
                        </Tooltip>
                      )}
                    </div>
                  </TooltipProvider>
                </TableCell>
              )}
              <TableCell>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                      <MoreHorizontal className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => onLogCall(customer)}>
                      Log Call/Note
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onSendEmail(customer)}>
                      Send Reminder Email
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onViewCustomer(customer)}>
                      View Full Customer Detail
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onCreatePromise(customer)}>
                      Create Promise to Pay
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => onAssignClerk(customer)}>
                      Assign to Clerk
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
